class SodCollectionAvailability extends HTMLElement {
  connectedCallback() {
    this.sunFilter = 'filter.p.m.super-sod.shade_tolerance'
    this.requests = new Map()
    this.counts = new Map()
    this.zipRequests = new Map()
    this.pages = new Map()
    this.revision = 0
    this.renderRevision = 0
    this.events = new AbortController()
    const options = { signal: this.events.signal }
    this.grid = document.getElementById('ProductGridContainer')
    this.section = document.getElementById('product-grid').dataset.id
    if (!this.themeSun || !new URLSearchParams(location.search).has(this.sunFilter)) {
      this.renderedParams = this.serverParams(location.search).toString()
      const cards = Array.from(this.grid.querySelectorAll('.product-card-wrapper'))
      if (cards.length === Number(this.dataset.productsCount)) {
        this.requests.set(this.productQuery(location.search).toString(), Promise.resolve(cards.map(card => this.record(card))))
      }
    }
    for (const event of ['setZipCode', 'setSelectedLocation', 'orderLocations']) {
      document.addEventListener(event, () => this.refresh(), options)
    }
    document.addEventListener('filter:updated', () => {
      requestAnimationFrame(() => this.refresh())
    }, options)
    this.addEventListener('click', event => {
      if (event.target.closest('[data-ca-group-button], [data-ca-more-toggle], [data-ca-more-group], [data-ca-open-sheet]')) {
        queueMicrotask(() => this.refresh())
      }
    }, options)
    this.grid.addEventListener('click', event => this.openLocation(event), { ...options, capture: true })
    this.observer = new MutationObserver(() => {
      cancelAnimationFrame(this.paintFrame)
      this.paintFrame = requestAnimationFrame(() => this.paint())
    })
    this.observer.observe(this.grid, { subtree: true, attributes: true, attributeFilter: ['data-blacklisted', 'data-delivery-available'] })
    customElements.whenDefined('facet-filters-form').then(() => {
      if (!this.isConnected) return
      if (this.themeSun && new URLSearchParams(location.search).has(this.sunFilter)) {
        this.renderPage(location.search.slice(1), false)
      } else this.refresh()
    })
  }

  disconnectedCallback() {
    this.revision++
    this.events.abort()
    this.observer.disconnect()
    cancelAnimationFrame(this.paintFrame)
  }

  get themeSun() { return this.dataset.themeSun === 'true' }
  get state() { return window.Scoutside?.LocationState || {} }
  id(value) { return String(value ?? '').split('/').pop() }

  parseBlacklist(data) {
    const products = Array.isArray(data) ? data : data?.products
    if (!Array.isArray(products) || products.some(value => !['string', 'number'].includes(typeof value))) {
      throw new Error('Invalid ZIP availability response')
    }
    return new Set(products.map(value => this.id(value)))
  }

  async blacklist() {
    const zip = String(this.state.zipcode || '')
    if (!zip) throw new Error('ZIP availability is not ready')
    const local = window.Scoutside?.ZipBlacklist?.listForZip?.(zip)
    if (local) return this.parseBlacklist(local)
    if (!this.zipRequests.has(zip)) {
      try {
        if (localStorage.getItem('SS:ca-sod-005-nav-blacklist-zip') === zip) {
          const blocked = this.parseBlacklist(JSON.parse(localStorage.getItem('SS:ca-sod-005-nav-blacklist-products')))
          this.zipRequests.set(zip, Promise.resolve(blocked))
        }
      } catch {
        // Missing or invalid navigation cache falls back to the Sod request.
      }
    }
    if (!this.zipRequests.has(zip)) {
      const request = (async () => {
        const base = window.Scoutside?.shop?.zipBlacklistApiUrl
        if (!base) throw new Error('ZIP availability service is missing')
        const response = await fetch(`${base.replace(/\/$/, '')}/${encodeURIComponent(zip)}`, { signal: this.events.signal })
        const blocked = this.parseBlacklist(await response.json())
        // This service uses 404 with [] for ZIPs without delivery exclusions.
        if (!response.ok && !(response.status === 404 && blocked.size === 0)) {
          throw new Error('Unable to load ZIP availability')
        }
        return blocked
      })().catch(error => {
        this.zipRequests.delete(zip)
        throw error
      })
      this.zipRequests.set(zip, request)
    }
    return this.zipRequests.get(zip)
  }

  available(product, blocked, store = this.id(this.state.selectedLocation)) {
    if (window.Scoutside?.ZipBlacklist?.hardHidden?.().includes(this.id(product.id))) return false
    return !blocked.has(this.id(product.id)) ||
      (Boolean(store) && !product.oos && product.pickups.some(value => this.id(value) === store))
  }

  matchesSun(product, params) {
    if (!this.themeSun) return true
    const selected = params.getAll(this.sunFilter).flatMap(value => value.split(',')).map(value => this.id(value))
    if (!selected.length) return true
    return Array.from(this.querySelectorAll('[data-sun-exposure]')).some(input =>
      selected.includes(this.id(input.value)) && product.sunExposure.includes(input.dataset.sunExposure))
  }

  serverParams(params) {
    const query = new URLSearchParams(params)
    if (this.themeSun) query.delete(this.sunFilter)
    query.sort()
    return query
  }

  async renderPage(search, updateHistory = true) {
    const facets = customElements.get('facet-filters-form')
    const revision = ++this.renderRevision
    this.revision++
    facets.searchParamsPrev = search
    if (updateHistory) facets.updateURLHash(search)
    const params = this.serverParams(search)
    const renderedParams = params.toString()
    if (!this.loading && renderedParams === this.renderedParams) {
      this.refresh()
      return
    }
    this.visibility = null
    this.loading = true
    this.setAttribute('aria-busy', 'true')
    this.grid.querySelector('.collection')?.classList.add('loading')
    params.set('section_id', this.section)
    const url = `${this.dataset.collectionUrl}?${params}`
    try {
      if (!this.pages.has(url)) {
        this.pages.set(url, fetch(url, { signal: this.events.signal }).then(response => {
          if (!response.ok) throw new Error('Unable to load sod products')
          return response.text()
        }).catch(error => { this.pages.delete(url); throw error }))
      }
      const html = await this.pages.get(url)
      if (!this.isConnected || revision !== this.renderRevision) return
      facets.renderFilters(html, null)
      facets.renderProductGridContainer(html)
      this.renderedParams = renderedParams
      this.loading = false
      this.syncSun()
      document.dispatchEvent(new CustomEvent('filter:updated'))
    } catch (error) {
      if (!this.isConnected || revision !== this.renderRevision) return
      this.loading = false
      this.grid.querySelector('.collection')?.classList.remove('loading')
      this.removeAttribute('aria-busy')
      this.querySelector('.ca-sod-006-product-count').textContent = 'Unable to load products'
      console.error('Sod filters:', error)
    }
  }

  toggleSun(value) {
    const params = new URLSearchParams(location.search)
    const selected = params.getAll(this.sunFilter).flatMap(item => item.split(','))
    params.delete(this.sunFilter)
    const next = selected.some(item => this.id(item) === this.id(value))
      ? selected.filter(item => this.id(item) !== this.id(value)) : [...selected, value]
    next.forEach(item => params.append(this.sunFilter, item))
    params.delete('page')
    this.renderPage(params.toString())
  }

  syncSun() {
    if (!this.themeSun) return
    if (this.syncedSunSearch === location.search && this.syncedSunRoot === this.firstElementChild) return
    this.syncedSunSearch = location.search
    this.syncedSunRoot = this.firstElementChild
    const selected = new URLSearchParams(location.search).getAll(this.sunFilter).flatMap(value => value.split(',')).map(value => this.id(value))
    const inputs = Array.from(this.querySelectorAll('[data-sun-exposure]'))
    inputs.forEach(input => { input.checked = selected.includes(this.id(input.value)); if (input.checked) input.disabled = false })
    document.querySelectorAll('#FacetFiltersForm input, #FacetFiltersFormMobile input').forEach(input => {
      if (input.name === this.sunFilter) {
        input.checked = selected.includes(this.id(input.value))
        if (input.checked) input.disabled = false
      }
    })
    this.querySelectorAll('[data-ca-group-button]').forEach(button => {
      if (button.dataset.group !== this.sunFilter) return
      button.classList.toggle('ca-sod-006-pill-selected', selected.length > 0)
      button.querySelector('span').textContent = `Sun & Shade${selected.length ? ` · ${selected.length}` : ''}`
    })
    this.querySelectorAll('[data-ca-remove-option]').forEach(button => {
      if (button.dataset.group === this.sunFilter) button.remove()
    })
    this.querySelector('[data-sod-sun-chips]')?.remove()
    this.querySelectorAll('.ca-sod-006-applied').forEach(section => {
      if (!section.querySelector('[data-ca-remove-option]')) section.remove()
    })
    if (!selected.length) return
    const chips = document.createElement('section')
    chips.className = 'ca-sod-006-applied'
    chips.dataset.sodSunChips = ''
    chips.setAttribute('aria-label', 'Applied sun and shade filters')
    const values = new Map(inputs.filter(input => input.checked).map(input => [input.value, input.dataset.sunExposure]))
    for (const [value, label] of values) {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'ca-sod-006-chip'
      button.dataset.caRemoveOption = ''
      button.dataset.group = this.sunFilter
      button.dataset.value = value
      button.setAttribute('aria-label', `Remove ${label}`)
      button.textContent = `Sun & Shade: ${label} ×`
      chips.append(button)
    }
    const clear = document.createElement('button')
    clear.type = 'button'
    clear.className = 'ca-sod-006-clear'
    clear.dataset.caClear = ''
    clear.textContent = 'Clear all'
    chips.append(clear)
    this.querySelector('[data-ca-sheet-overlay]').before(chips)
  }

  productQuery(params) {
    const query = this.serverParams(params)
    for (const name of ['page', 'sort_by', 'section_id']) query.delete(name)
    query.set('section_id', 'ca-sod-006-availability')
    query.sort()
    return query
  }

  products(params) {
    const query = this.productQuery(params)
    const key = query.toString()
    if (!this.requests.has(key)) {
      const url = new URL(this.dataset.collectionUrl, location.origin)
      url.search = key
      this.requests.set(key, this.fetchProducts(url).catch(error => { this.requests.delete(key); throw error }))
    }
    return this.requests.get(key)
  }

  async fetchProducts(url) {
    const products = []
    do {
      const response = await fetch(url, { signal: this.events.signal })
      if (!response.ok) throw new Error('Unable to load product availability')
      const html = new DOMParser().parseFromString(await response.text(), 'text/html')
      const data = JSON.parse(html.querySelector('[data-sod-availability]')?.textContent || 'null')
      if (!Array.isArray(data?.products)) throw new Error('Product availability data is missing')
      products.push(...data.products)
      url = data.next ? new URL(data.next, location.origin) : null
      if (url) url.searchParams.set('section_id', 'ca-sod-006-availability')
    } while (url)
    return products
  }

  countKey(params) {
    const query = new URLSearchParams(params)
    for (const name of ['page', 'sort_by', 'section_id']) query.delete(name)
    query.sort()
    return JSON.stringify([query.toString(), String(this.state.zipcode || ''), this.id(this.state.selectedLocation)])
  }

  async count(params, availability) {
    const key = this.countKey(params)
    if (this.counts.has(key)) return this.counts.get(key)
    const store = this.id(this.state.selectedLocation)
    const [products, blocked] = await Promise.all([this.products(params), availability])
    const query = new URLSearchParams(params)
    const count = products.filter(product => this.available(product, blocked, store) && this.matchesSun(product, query)).length
    this.counts.set(key, count)
    return count
  }

  record(card) {
    const data = card.querySelector('[data-available-pickup-locations]')?.dataset
    return {
      id: card.dataset.productId,
      oos: card.hasAttribute('data-oos-message'),
      pickups: data && (data.productType === 'Sod' || data.hasPickupVariant === 'true') ? JSON.parse(data.availablePickupLocations || '[]') : [],
      sunExposure: JSON.parse(card.dataset.sodExposure || '[]'),
    }
  }

  paint() {
    if (!this.visibility || this.loading) return
    const grid = document.getElementById('product-grid')
    const params = new URLSearchParams(location.search)
    let any = false
    grid.querySelectorAll('.product-card-wrapper').forEach(card => {
      const product = this.record(card)
      const visible = this.available(product, this.visibility) && this.matchesSun(product, params)
      card.dataset.sodAvailable = String(visible)
      any ||= visible
    })
    grid.classList.toggle('hidden', !any)
    document.getElementById('no-results')?.classList.toggle('hidden', any)
  }

  openLocation(event) {
    const trigger = event.target.closest('.zipcode-modal-toggler, .store-selector-toggler')
    if (!trigger) return
    event.preventDefault()
    event.stopPropagation()
    const state = this.state
    if (trigger.matches('.zipcode-modal-toggler')) return state.toggleZipcodeModal()
    if (window.Scoutside?.cart?.items?.some(window.Scoutside.CartHelpers.isPickupItem)) {
      state.openZipcodeModal({ forceWarning: true, onProceed: () => { state.closeZipcodeModal(); state.toggleStoreSelectorDrawer() } })
    } else state.toggleStoreSelectorDrawer()
  }

  async refresh() {
    if (this.loading || !this.isConnected) return
    this.syncSun()
    const revision = ++this.revision
    const search = location.search
    const current = () => this.isConnected && revision === this.revision && search === location.search
    const total = this.querySelector('.ca-sod-006-product-count')
    const show = this.querySelector('.ca-sod-006-show-products')
    const inputs = Array.from(this.querySelectorAll('[data-ca-option]'))
    const visible = inputs.filter(input => !input.closest('[hidden]'))
    const options = new Map(visible.map(input => [JSON.stringify([input.dataset.group, input.value]), input]))
    this.setAttribute('aria-busy', 'true')
    const zip = String(this.state.zipcode || '')
    if (zip !== this.visibilityZip) this.visibility = null
    else this.paint()
    if (!this.counts.has(this.countKey(search))) {
      total.textContent = '…'
      show.textContent = 'Show products'
    }
    try {
      const availability = this.blacklist()
      const jobs = [availability.then(blocked => {
        if (!current()) return
        this.visibility = blocked
        this.visibilityZip = zip
        this.paint()
      }), this.count(search, availability).then(count => {
        if (!current()) return
        const label = `${count} product${count === 1 ? '' : 's'}`
        total.textContent = label
        show.textContent = `Show ${label}`
      })]
      for (const input of options.values()) {
        const name = input.dataset.group
        const duplicates = inputs.filter(item => item.dataset.group === name && item.value === input.value)
        const params = new URLSearchParams(search)
        if (input.dataset.operator !== 'AND') params.delete(name)
        if (!params.getAll(name).includes(input.value)) params.append(name, input.value)
        if (!this.counts.has(this.countKey(params))) {
          duplicates.forEach(item => { item.closest('label').querySelector('.ca-sod-006-count').textContent = '…' })
        }
        jobs.push(this.count(params, availability).then(count => {
          if (!current()) return
          duplicates.forEach(item => {
            item.closest('label').querySelector('.ca-sod-006-count').textContent = String(count)
            item.disabled = !item.checked && count === 0
          })
          document.querySelectorAll('#FacetFiltersForm input, #FacetFiltersFormMobile input').forEach(item => {
            if (item.name === name && item.value === input.value) item.disabled = !item.checked && count === 0
          })
        }))
      }
      await Promise.all(jobs)
    } catch (error) {
      if (!current()) return
      total.textContent = '—'
      visible.forEach(input => { input.closest('label').querySelector('.ca-sod-006-count').textContent = '—' })
      console.error('Sod availability:', error)
    } finally {
      if (current()) this.removeAttribute('aria-busy')
    }
  }
}

customElements.define('sod-collection-availability', SodCollectionAvailability)
